import { Component, Input } from '@angular/core';
import { LoadingSpinnerSize } from './loading-spinner-size.type';

/** Standard loading indicator (Tailwind ring). Prefer over `mat-spinner`. */
@Component({
  selector: 'app-loading-spinner',
  standalone: true,
  templateUrl: './loading-spinner.component.html',
  host: {
    'attr.data-loading-spinner': 'true',
    '[attr.role]': 'hostRole',
    '[attr.aria-label]': 'hostAriaLabel',
  },
})
export class LoadingSpinnerComponent {
  /** Optional visible caption under the ring (e.g. translated "Loading…"). */
  @Input() label: string | null = null;

  /** Ring size: sm (24px), md (40px), lg (56px). */
  @Input() size: LoadingSpinnerSize = 'md';

  /**
   * When false (default): column flex, for use inside a parent that already centers.
   * When true: `inline-flex` for compact placement (e.g. toolbar).
   */
  @Input() inline = false;

  /**
   * When true with `inline === false`, root stretches `h-full w-full` inside a sized parent.
   */
  @Input() fill = false;

  /** Screen reader label when `label` is not shown (e.g. overlay with no text). */
  @Input() ariaLabel: string | null = null;

  get hostRole(): string | null {
    return this.label || this.ariaLabel ? 'status' : null;
  }

  get hostAriaLabel(): string | null {
    if (this.label) {
      return null;
    }
    return this.ariaLabel;
  }

  get wrapperClass(): string {
    const base = 'flex flex-col items-center justify-center gap-3';
    if (this.inline) {
      return `${base} inline-flex`;
    }
    if (this.fill) {
      return `${base} h-full w-full`;
    }
    return base;
  }

  get ringClass(): string {
    const spin = 'shrink-0 animate-spin rounded-t-full border-solid border-t-line border-t-blue-600 dark:border-t-blue-400';
    const bySize: Record<LoadingSpinnerSize, string> = {
      sm: 'h-6 w-6 border-2',
      md: 'h-10 w-10 border-2',
      lg: 'h-14 w-14 border-2',
    };
    return `${bySize[this.size]} ${spin}`;
  }
}
